const STAGING_HOST = 'wac.stg.internal.atlassian.com';
const DEFAULT_DELAY_MS = 45_000;
const MAX_DELAY_MS = 120_000;

type StagingScriptOptions = {
	name: string;
	load: () => void;
	canLoad?: () => boolean;
};

/**
 * Opt-in staging diagnostic on any route. Only explicitly integrated loaders participate.
 * Example: ?wac_script_timing=defer&wac_defer_scripts=gtm&wac_script_delay_ms=45000
 * Callers retain normal consent checks and can recheck eligibility before deferred release.
 */
export function scheduleStagingScript({ name, load, canLoad }: StagingScriptOptions): void {
	if (
		typeof window === 'undefined' ||
		window.location.protocol !== 'https:' ||
		window.location.hostname !== STAGING_HOST
	) {
		load();
		return;
	}

	const params = new URLSearchParams(window.location.search);
	const selected = (params.get('wac_defer_scripts') ?? '').split(',').map((value) => value.trim());
	const timing = params.get('wac_script_timing');
	if (!selected.includes(name) || (timing !== 'control' && timing !== 'defer')) {
		load();
		return;
	}
	const markPrefix = `wac:staging-script:${name}`;
	if (timing === 'control') {
		performance.mark(`${markPrefix}:control`);
		load();
		return;
	}

	const requestedDelay = params.get('wac_script_delay_ms');
	const delay = requestedDelay === null ? DEFAULT_DELAY_MS : Number(requestedDelay);
	if (
		(requestedDelay !== null && !/^\d+$/.test(requestedDelay)) ||
		!Number.isSafeInteger(delay) ||
		delay > MAX_DELAY_MS
	) {
		// Invalid diagnostic settings keep normal loading behavior.
		load();
		return;
	}

	performance.mark(`${markPrefix}:deferred`);
	const release = () => {
		if (canLoad && !canLoad()) {
			performance.mark(`${markPrefix}:skipped`);
			return;
		}
		performance.mark(`${markPrefix}:released`);
		load();
	};
	// Navigation-relative timing makes the initial observation window explicit.
	const remaining = delay - performance.now();
	if (remaining <= 0) {
		release();
	} else {
		window.setTimeout(release, remaining);
	}
}
